(() => {
  'use strict';
  const scenes = new Map();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const compact = window.matchMedia('(max-width: 760px)');
  const canObserve = typeof window.IntersectionObserver === 'function' && typeof window.MutationObserver === 'function';
  let frame = null;
  let watching = false;
  let bodyObserver = null;

  function stopped() {
    return reduced.matches || document.hidden || document.body.hasAttribute('data-motion-paused');
  }

  function cancelFrame() {
    if (frame !== null) window.cancelAnimationFrame(frame);
    frame = null;
  }

  function scheduleFrame() {
    if (stopped() || !Array.from(scenes.values()).some((scene) => scene.active)) {
      cancelFrame();
      return;
    }
    if (frame !== null) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      if (stopped()) return;
      const height = window.innerHeight || document.documentElement.clientHeight;
      const visible = Array.from(scenes.values()).filter((scene) => scene.active)
        .map((scene) => ({ scene, rect: scene.root.getBoundingClientRect() }));
      visible.forEach(({ scene, rect }) => {
        const progress = Math.max(0, Math.min(1, (height - rect.top) / (height + rect.height)));
        scene.drift((progress * 2 - 1) * (compact.matches ? 0.5 : 1));
      });
    });
  }

  function sync() {
    scenes.forEach((scene) => scene.sync());
    scheduleFrame();
  }

  function watch() {
    if (watching) return;
    watching = true;
    window.addEventListener('scroll', scheduleFrame, { passive: true });
    window.addEventListener('resize', scheduleFrame, { passive: true });
    document.addEventListener('visibilitychange', sync, { passive: true });
    reduced.addEventListener('change', sync, { passive: true });
    compact.addEventListener('change', scheduleFrame, { passive: true });
    bodyObserver = new window.MutationObserver(sync);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['data-motion-paused'] });
  }

  function unwatchIfEmpty() {
    if (!watching || Array.from(scenes.values()).some((scene) => scene.animated)) return;
    watching = false;
    cancelFrame();
    window.removeEventListener('scroll', scheduleFrame);
    window.removeEventListener('resize', scheduleFrame);
    document.removeEventListener('visibilitychange', sync);
    reduced.removeEventListener('change', sync);
    compact.removeEventListener('change', scheduleFrame);
    bodyObserver.disconnect();
    bodyObserver = null;
  }

  function mount(root) {
    if (scenes.has(root)) return;
    const owned = (element) => element.closest('[data-fn-scene]') === root;
    const entrants = Array.from(root.querySelectorAll('[data-fn-enter]')).filter(owned);
    const decorations = Array.from(root.querySelectorAll('[data-fn-drift]')).filter(owned)
      .filter((element) => ['backdrop', 'bottle', 'orbit'].includes(element.dataset.fnDrift));
    const editor = Boolean(window.Shopify?.designMode || root.dataset.designMode === 'true');
    let mounted = true;
    let intersecting = false;
    let sceneObserver = null;
    let entryObserver = null;

    function reveal(element, settled = false) {
      element.setAttribute('data-fn-seen', '');
      if (settled) element.setAttribute('data-fn-settled', '');
      entryObserver?.unobserve(element);
    }

    function clearDrift() {
      decorations.forEach((element) => element.style.removeProperty('transform'));
    }

    function focus(event) {
      const entrant = event.target.closest?.('[data-fn-enter]');
      if (entrants.includes(entrant)) reveal(entrant, true);
    }

    const scene = {
      root,
      animated: canObserve && !editor,
      active: false,
      sync() {
        if (!scene.animated || stopped()) entrants.forEach((element) => reveal(element, true));
        scene.active = scene.animated && intersecting && !stopped();
        root.toggleAttribute('data-fn-active', scene.active);
        if (!scene.active) clearDrift();
      },
      drift(progress) {
        decorations.forEach((element) => {
          if (element.dataset.fnDrift === 'backdrop') element.style.transform = `translateX(${(26 * progress).toFixed(2)}px)`;
          if (element.dataset.fnDrift === 'bottle') element.style.transform = `translateY(${(-22 * progress).toFixed(2)}px) rotate(${(-6 * progress).toFixed(2)}deg)`;
          if (element.dataset.fnDrift === 'orbit') element.style.transform = `rotate(${(35 * progress).toFixed(2)}deg)`;
        });
      },
      destroy() {
        mounted = false;
        sceneObserver?.disconnect();
        entryObserver?.disconnect();
        root.removeEventListener('focusin', focus);
        clearDrift();
        root.removeAttribute('data-fn-motion-ready');
        root.removeAttribute('data-fn-active');
        scenes.delete(root);
        unwatchIfEmpty();
        scheduleFrame();
      },
    };
    scenes.set(root, scene);
    root.addEventListener('focusin', focus, { passive: true });
    if (scene.animated) {
      sceneObserver = new window.IntersectionObserver((entries) => {
        if (!mounted) return;
        entries.forEach((entry) => {
          if (entry.target === root) intersecting = entry.isIntersecting;
        });
        scene.sync();
        scheduleFrame();
      }, { threshold: 0 });
      entryObserver = new window.IntersectionObserver((entries) => {
        if (!mounted) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.12) reveal(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -32px 0px' });
      sceneObserver.observe(root);
      entrants.filter((element) => !element.hasAttribute('data-fn-seen')).forEach((element) => entryObserver.observe(element));
      scene.sync();
      root.setAttribute('data-fn-motion-ready', '');
      watch();
    } else scene.sync();
  }

  function mountWithin(container) {
    if (container.matches?.('[data-fn-scene]')) mount(container);
    container.querySelectorAll('[data-fn-scene]').forEach(mount);
  }

  document.addEventListener('shopify:section:load', (event) => mountWithin(event.target), { passive: true });
  document.addEventListener('shopify:section:unload', (event) => {
    scenes.forEach((scene, root) => {
      if (event.target === root || event.target.contains(root)) scene.destroy();
    });
  }, { passive: true });
  mountWithin(document);
})();
